import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../services/auth-service';
import Modal from '../../components/Modal';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Applicant {
  _id: string;
  name: string;
  email: string;
  userType: 'partner' | 'associate' | 'investor';
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  partner?: { institution?: string; titleRole?: string; studentCount?: string };
  associate?: { organization?: string; titleRole?: string; purpose?: string };
  investor?: { fundName?: string; titleRole?: string; checkSize?: string };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const APPLICATION_ROLES = ['partner', 'associate', 'investor'];

const ROLE_COLORS: Record<string, string> = {
  partner:   'bg-success/15 text-success',
  associate: 'bg-warning/15 text-warning',
  investor:  'bg-chart/15 text-chart',
};

const STATUS_COLORS: Record<string, string> = {
  pending:  'bg-warning/15 text-warning',
  approved: 'bg-success/15 text-success',
  rejected: 'bg-danger/15 text-danger',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function roleFields(a: Applicant): { label: string; value: string }[] {
  if (a.userType === 'partner') {
    const p = a.partner || {};
    return [
      { label: 'Institution', value: p.institution || '—' },
      { label: 'Title / Role', value: p.titleRole || '—' },
      { label: 'Student founders', value: p.studentCount || '—' },
    ];
  }
  if (a.userType === 'associate') {
    const as = a.associate || {};
    return [
      { label: 'Organization', value: as.organization || '—' },
      { label: 'Role / Title', value: as.titleRole || '—' },
      { label: 'Purpose of access', value: as.purpose || '—' },
    ];
  }
  if (a.userType === 'investor') {
    const i = a.investor || {};
    return [
      { label: 'Fund / Firm', value: i.fundName || '—' },
      { label: 'Role / Title', value: i.titleRole || '—' },
      { label: 'Check size', value: i.checkSize || '—' },
    ];
  }
  return [];
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

const DetailModal: React.FC<{
  applicant: Applicant;
  onClose: () => void;
  onApprove: (a: Applicant) => void;
  onDeny: (a: Applicant) => void;
}> = ({ applicant, onClose, onApprove, onDeny }) => {
  const fields = roleFields(applicant);
  const isPending = applicant.status === 'pending';
  return (
    <Modal isOpen title={applicant.name} onClose={onClose}>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${ROLE_COLORS[applicant.userType] || 'bg-surface text-muted'}`}>
            {applicant.userType}
          </span>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[applicant.status] || 'bg-surface text-muted'}`}>
            {applicant.status}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-muted mb-1">Email</div>
            <div className="text-sm text-text">{applicant.email}</div>
          </div>
          <div>
            <div className="text-xs text-muted mb-1">Applied</div>
            <div className="text-sm text-text">{formatDate(applicant.createdAt)}</div>
          </div>
        </div>

        {fields.length > 0 && (
          <div className="border-t border-line pt-4">
            <div className="text-xs font-medium text-muted uppercase tracking-wide mb-3">Profile Details</div>
            <div className="space-y-3">
              {fields.map(f => (
                <div key={f.label} className="grid grid-cols-2 gap-3">
                  <div className="text-xs text-muted">{f.label}</div>
                  <div className="text-sm text-text">{f.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isPending && (
          <div className="flex gap-3 pt-2 border-t border-line">
            <button
              onClick={() => { onClose(); onApprove(applicant); }}
              className="flex-1 px-4 py-2.5 rounded-card bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors"
            >
              Approve
            </button>
            <button
              onClick={() => { onClose(); onDeny(applicant); }}
              className="flex-1 px-4 py-2.5 rounded-card bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors"
            >
              Deny
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

// ─── Approve Confirm Modal ────────────────────────────────────────────────────

const ApproveModal: React.FC<{
  applicant: Applicant;
  onClose: () => void;
  onConfirm: (id: string) => void;
  loading: boolean;
}> = ({ applicant, onClose, onConfirm, loading }) => (
  <Modal isOpen title="Approve Application" onClose={onClose}>
    <div className="space-y-4">
      <p className="text-sm text-muted">
        Approve <strong className="text-text">{applicant.name}</strong>'s application as a{' '}
        <strong className="text-text capitalize">{applicant.userType}</strong>? They will receive an email and gain access to their dashboard.
      </p>
      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2.5 rounded-card border border-line text-sm font-medium text-text hover:bg-surface transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={() => onConfirm(applicant._id)}
          disabled={loading}
          className="flex-1 px-4 py-2.5 rounded-card bg-success text-white text-sm font-medium hover:bg-success/90 transition-colors disabled:opacity-60"
        >
          {loading ? 'Approving…' : 'Approve'}
        </button>
      </div>
    </div>
  </Modal>
);

// ─── Deny Confirm Modal ───────────────────────────────────────────────────────

const DenyModal: React.FC<{
  applicant: Applicant;
  onClose: () => void;
  onConfirm: (id: string, reason: string) => void;
  loading: boolean;
}> = ({ applicant, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState('');
  return (
    <Modal isOpen title="Deny Application" onClose={onClose}>
      <div className="space-y-4">
        <p className="text-sm text-muted">
          Deny <strong className="text-text">{applicant.name}</strong>'s application. They will be notified via email.
        </p>
        <div>
          <label className="block text-xs font-medium text-text mb-1.5">Reason (optional)</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="Provide a reason for the applicant..."
            className="w-full px-4 py-2.5 rounded-card border border-line bg-shell text-text placeholder:text-muted/50 focus:outline-none focus:ring-2 focus:ring-chart/30 focus:border-chart text-sm resize-none"
          />
        </div>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-card border border-line text-sm font-medium text-text hover:bg-surface transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(applicant._id, reason)}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-card bg-danger text-white text-sm font-medium hover:bg-danger/90 transition-colors disabled:opacity-60"
          >
            {loading ? 'Denying…' : 'Deny Application'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const Applications: React.FC = () => {
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'pending' | 'reviewed'>('pending');

  const [detail, setDetail] = useState<Applicant | null>(null);
  const [approveTarget, setApproveTarget] = useState<Applicant | null>(null);
  const [denyTarget, setDenyTarget] = useState<Applicant | null>(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await apiFetch.get<{ users: Applicant[] }>('/users?limit=500');
        if (!alive) return;
        const filtered = (r.users || []).filter(u => APPLICATION_ROLES.includes(u.userType));
        setApplicants(filtered);
      } catch {
        if (alive) setError('Failed to load applications.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const pending  = applicants.filter(a => a.status === 'pending');
  const reviewed = applicants.filter(a => a.status === 'approved' || a.status === 'rejected');
  const displayed = tab === 'pending' ? pending : reviewed;

  const handleApprove = async (id: string) => {
    setActing(true);
    try {
      await apiFetch.post(`/users/admin/${id}/approve`);
      setApplicants(prev => prev.map(a => a._id === id ? { ...a, status: 'approved' } : a));
    } catch {
      // silent fail
    } finally {
      setActing(false);
      setApproveTarget(null);
    }
  };

  const handleDeny = async (id: string, reason: string) => {
    setActing(true);
    try {
      await apiFetch.post(`/users/admin/${id}/reject`, { reason });
      setApplicants(prev => prev.map(a => a._id === id ? { ...a, status: 'rejected' } : a));
    } catch {
      // silent fail
    } finally {
      setActing(false);
      setDenyTarget(null);
    }
  };

  return (
    <div className="section-gap">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl tracking-wide text-text uppercase">Applications</h1>
          <p className="text-muted text-sm mt-1">Partner, Associate, and Investor applications pending review.</p>
        </div>
        {pending.length > 0 && (
          <span className="px-3 py-1 rounded-full bg-warning/15 text-warning text-sm font-semibold">
            {pending.length} pending
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-line">
        {(['pending', 'reviewed'] as const).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium capitalize border-b-2 -mb-px transition-colors ${
              tab === t ? 'border-chart text-chart' : 'border-transparent text-muted hover:text-text'
            }`}
          >
            {t === 'pending' ? `Pending (${pending.length})` : `Reviewed (${reviewed.length})`}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted text-sm">Loading…</div>
      ) : error ? (
        <div className="flex items-center justify-center py-20 text-danger text-sm">{error}</div>
      ) : displayed.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-muted text-sm">
          {tab === 'pending' ? 'No pending applications.' : 'No reviewed applications yet.'}
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className="text-left px-5 py-3 text-xs font-medium text-muted uppercase tracking-wide">Name</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-muted uppercase tracking-wide">Email</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-muted uppercase tracking-wide">Role</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-muted uppercase tracking-wide">Applied</th>
                <th className="text-left px-5 py-3 text-xs font-medium text-muted uppercase tracking-wide">Status</th>
                <th className="text-right px-5 py-3 text-xs font-medium text-muted uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((a, i) => (
                <tr key={a._id} className={`border-b border-line last:border-0 hover:bg-surface/40 transition-colors ${i % 2 === 0 ? '' : 'bg-surface/20'}`}>
                  <td className="px-5 py-3.5 font-medium text-text">{a.name}</td>
                  <td className="px-5 py-3.5 text-muted">{a.email}</td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${ROLE_COLORS[a.userType] || 'bg-surface text-muted'}`}>
                      {a.userType}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-muted">{formatDate(a.createdAt)}</td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[a.status] || 'bg-surface text-muted'}`}>
                      {a.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setDetail(a)}
                        className="px-3 py-1.5 rounded-card border border-line text-xs font-medium text-text hover:bg-surface transition-colors"
                      >
                        Details
                      </button>
                      {a.status === 'pending' && (
                        <>
                          <button
                            onClick={() => setApproveTarget(a)}
                            className="px-3 py-1.5 rounded-card bg-success/10 text-success text-xs font-medium hover:bg-success/20 transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => setDenyTarget(a)}
                            className="px-3 py-1.5 rounded-card bg-danger/10 text-danger text-xs font-medium hover:bg-danger/20 transition-colors"
                          >
                            Deny
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <DetailModal
          applicant={detail}
          onClose={() => setDetail(null)}
          onApprove={a => setApproveTarget(a)}
          onDeny={a => setDenyTarget(a)}
        />
      )}

      {approveTarget && (
        <ApproveModal
          applicant={approveTarget}
          onClose={() => setApproveTarget(null)}
          onConfirm={handleApprove}
          loading={acting}
        />
      )}

      {denyTarget && (
        <DenyModal
          applicant={denyTarget}
          onClose={() => setDenyTarget(null)}
          onConfirm={handleDeny}
          loading={acting}
        />
      )}
    </div>
  );
};

export default Applications;
